import React, { useEffect, useMemo, useRef } from 'react';
import { ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { platformLabel, type RelayClientInfo } from '../../services/relay';
import type { SandboxImage, SandboxProfile } from '../../types/cowork';
import { buildCloudEnvOptions, findCloudEnvOption } from '../../utils/sandboxEnv';

const SandboxIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

interface ExecutionEnvPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  profiles: SandboxProfile[];
  images: SandboxImage[];
  selectedProfileId: string | null;
  selectedImageId: string | null;
  /** 云端沙箱选项：运行环境与模板一次定完，调用方不必再各选一次。 */
  onSelectCloud: (profileId: string | null, imageId: string | null) => void;
  /** 传了才渲染「桌面客户端」分组——定时任务只在云端跑，不给这一组。 */
  onSelectDesktop?: (clientId: string) => void;
  relayClients?: RelayClientInfo[];
  selectedDesktopClientId?: string | null;
  onManage?: () => void;
  /** 'up' 贴在锚点上方（输入框工具条），'down' 贴在下方（表单字段）。 */
  placement?: 'up' | 'down';
}

const ExecutionEnvPopover: React.FC<ExecutionEnvPopoverProps> = ({
  isOpen,
  onClose,
  anchorRef,
  profiles,
  images,
  selectedProfileId,
  selectedImageId,
  onSelectCloud,
  onSelectDesktop,
  relayClients = [],
  selectedDesktopClientId,
  onManage,
  placement = 'up',
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!popoverRef.current?.contains(target) && !anchorRef.current?.contains(target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose, anchorRef]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const cloudOptions = useMemo(() => buildCloudEnvOptions(profiles, images), [profiles, images]);
  const activeCloud = selectedDesktopClientId
    ? null
    : findCloudEnvOption(cloudOptions, selectedProfileId, selectedImageId);
  const desktopClients = useMemo(
    () => relayClients.filter((client) => client.platform !== 'web' && client.online),
    [relayClients],
  );

  if (!isOpen) return null;

  const itemClass = (active: boolean) =>
    `w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors text-left dark:text-claude-darkText text-claude-text ${
      active
        ? 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover'
        : 'dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
    }`;

  const sectionHeaderClass = 'px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider dark:text-claude-darkTextSecondary text-claude-textSecondary';
  const groupHeaderClass = 'px-3 pt-2 pb-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary';

  let lastGroup: string | undefined;

  return (
    <div
      ref={popoverRef}
      className={`absolute left-0 w-72 max-h-[60vh] overflow-y-auto rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface shadow-lg z-50 ${
        placement === 'down' ? 'top-full mt-2' : 'bottom-full mb-2'
      }`}
    >
      <div className="px-3 py-2 border-b dark:border-claude-darkBorder border-claude-border">
        <p className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
          {i18nService.t('sandboxProfileSelectTitle')}
        </p>
      </div>

      <div className={sectionHeaderClass}>{i18nService.t('envSelectorCloudSandbox')}</div>
      {cloudOptions.length > 0 ? (
        cloudOptions.map((option) => {
          const showGroup = option.groupLabel && option.groupLabel !== lastGroup;
          lastGroup = option.groupLabel;
          const active = activeCloud?.key === option.key;
          return (
            <React.Fragment key={option.key}>
              {showGroup && <div className={groupHeaderClass}>{option.groupLabel}</div>}
              <button
                type="button"
                onClick={() => {
                  onSelectCloud(option.profileId, option.imageId);
                  onClose();
                }}
                className={itemClass(active)}
                title={option.description || undefined}
              >
                <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary flex-shrink-0">
                  <SandboxIcon className="h-4 w-4 flex-shrink-0" />
                </span>
                <span className="truncate flex-1">{option.label}</span>
                {active && <span className="text-xs text-claude-accent flex-shrink-0">✓</span>}
              </button>
            </React.Fragment>
          );
        })
      ) : (
        <div className="px-3 py-3 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary italic">
          {i18nService.t('sandboxProfileEmpty')}
        </div>
      )}

      {onSelectDesktop && (
        <>
          <div className="border-t dark:border-claude-darkBorder border-claude-border" />
          <div className={sectionHeaderClass}>{i18nService.t('envSelectorDesktopClient')}</div>
          {desktopClients.length > 0 ? (
            desktopClients.map((client) => (
              <button
                key={client.clientId}
                type="button"
                onClick={() => {
                  onSelectDesktop(client.clientId);
                  onClose();
                }}
                className={itemClass(selectedDesktopClientId === client.clientId)}
              >
                <ComputerDesktopIcon className="h-4 w-4 flex-shrink-0 text-green-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{client.deviceName || client.clientId.slice(0, 8)}</div>
                  <div className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                    {platformLabel(client.platform)} · v{client.appVersion}
                  </div>
                </div>
                <span className="w-2 h-2 rounded-full flex-shrink-0 bg-green-500" />
                {selectedDesktopClientId === client.clientId && (
                  <span className="text-xs text-claude-accent flex-shrink-0">✓</span>
                )}
              </button>
            ))
          ) : (
            <div className="px-3 py-3 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary italic text-center">
              {i18nService.t('relayNoDesktopOnline')}
            </div>
          )}
        </>
      )}

      {onManage && (
        <>
          <div className="border-t dark:border-claude-darkBorder border-claude-border" />
          <button
            type="button"
            onClick={() => { onManage(); onClose(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-b-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors text-left"
          >
            <span className="text-xs">⚙</span>
            <span>{i18nService.t('sandboxManage')}</span>
          </button>
        </>
      )}
    </div>
  );
};

export default ExecutionEnvPopover;
